/**
 * Drag-to-compare slider for the lit / unlit pair.
 *
 * A lamp is a two-state object and its whole value lives in the state you
 * cannot photograph in a catalogue shot. Showing both images side by side
 * asks the visitor to do the comparison in their head; overlaying them and
 * letting them wipe between the two does it for them.
 *
 * Progressive enhancement: without JS the markup is two stacked <img>s with
 * captions, which is a perfectly reasonable before/after. This class only
 * adds the wipe.
 */
class OvaranaCompare extends HTMLElement {
  #dragging = false;

  connectedCallback() {
    this.overlay = this.querySelector('[data-compare-overlay]');
    this.handle = this.querySelector('[data-compare-handle]');
    if (!this.overlay || !this.handle) return;

    this.classList.add('is-enhanced');

    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerUp);
    this.handle.addEventListener('keydown', this.#onKeyDown);

    this.#setPosition(Number(this.dataset.startPosition || 50));
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerUp);
    this.handle?.removeEventListener('keydown', this.#onKeyDown);
  }

  #onPointerDown = (event) => {
    this.#dragging = true;
    this.setPointerCapture(event.pointerId);
    this.#trackPointer(event);
  };

  #onPointerMove = (event) => {
    if (!this.#dragging) return;
    // Only prevent the page scroll once a drag is actually in progress, so a
    // vertical swipe that starts on the image still scrolls the page.
    event.preventDefault();
    this.#trackPointer(event);
  };

  #onPointerUp = (event) => {
    this.#dragging = false;
    if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
  };

  #onKeyDown = (event) => {
    const step = event.shiftKey ? 10 : 2;
    const current = Number(this.handle.getAttribute('aria-valuenow'));

    if (event.key === 'ArrowLeft') {
      this.#setPosition(current - step);
    } else if (event.key === 'ArrowRight') {
      this.#setPosition(current + step);
    } else if (event.key === 'Home') {
      this.#setPosition(0);
    } else if (event.key === 'End') {
      this.#setPosition(100);
    } else {
      return;
    }

    event.preventDefault();
  };

  #trackPointer(event) {
    const rect = this.getBoundingClientRect();
    this.#setPosition(((event.clientX - rect.left) / rect.width) * 100);
  }

  /** @param {number} value - Wipe position as a percentage. */
  #setPosition(value) {
    const clamped = Math.min(100, Math.max(0, value));
    this.style.setProperty('--compare-position', `${clamped}%`);
    this.handle.setAttribute('aria-valuenow', String(Math.round(clamped)));
  }
}

customElements.define('ovarana-compare', OvaranaCompare);
